import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CaretRightIcon, XIcon } from '@phosphor-icons/react';
import { alertKey, useAlertsStore } from '@/stores';
import { useFeatureFlagsState } from '@/hooks/useFeatureFlags';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { AlertNotificationCard } from './AlertNotificationCard';
import { AppRoutes } from '@/constants/routes';
import { URL_PARAMS, SortOptionUrl } from '@/utils/query-url-params';
import { SortOption } from '@/utils/query-filters';
import {
    CONTAINER_INITIAL,
    CONTAINER_ANIMATE,
    NOTIFICATION_FADE,
    VIEW_MORE_INITIAL,
    VIEW_MORE_EXIT,
    CARD_EXIT_REDUCED,
    getViewMoreTransition,
} from './animations';

const MAX_VISIBLE_NOTIFICATIONS = 3;
const RENDER_BUFFER = 1;

export function AlertNotifications() {
    const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.EVENTS]);
    const shouldReduceMotion = useReducedMotion();
    const { flags, isLoading: flagsLoading } = useFeatureFlagsState();
    const alertsEnabled = flags.alertsEnabled;

    const setAlertsEnabled = useAlertsStore((state) => state.setAlertsEnabled);

    useEffect(() => {
        if (flagsLoading) return;
        setAlertsEnabled(alertsEnabled);
    }, [flagsLoading, alertsEnabled, setAlertsEnabled]);

    const notifications = useAlertsStore((state) => state.notificationAlerts);
    const removeNotification = useAlertsStore(
        (state) => state.removeNotificationAlert,
    );
    const clearNotifications = useAlertsStore(
        (state) => state.clearNotificationAlerts,
    );

    const shouldRender =
        !flagsLoading && alertsEnabled && notifications.length > 0;

    const visibleNotifications = useMemo(
        () =>
            notifications
                .slice(-(MAX_VISIBLE_NOTIFICATIONS + RENDER_BUFFER))
                .reverse(),
        [notifications],
    );

    const totalCount = notifications.length;

    const handleViewAllAlerts = useCallback(() => {
        const searchParams = new URLSearchParams();
        searchParams.set(
            URL_PARAMS.SORT,
            SortOptionUrl[SortOption.DATE_NEWEST],
        );
        const newWindow = window.open(
            `${AppRoutes.ALERTS}?${searchParams.toString()}`,
            '_blank',
            'noopener',
        );
        if (newWindow) {
            clearNotifications();
        }
    }, [clearNotifications]);

    const handleViewAllKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleViewAllAlerts();
            }
        },
        [handleViewAllAlerts],
    );

    if (!shouldRender) {
        return null;
    }

    return (
        <motion.div
            initial={shouldReduceMotion ? false : CONTAINER_INITIAL}
            animate={CONTAINER_ANIMATE}
            transition={NOTIFICATION_FADE}
            className="fixed bottom-6 left-6 z-50 w-[360px] max-w-[calc(100vw-48px)]"
        >
            <div className="flex flex-col gap-2">
                <div className="flex max-h-[340px] flex-col overflow-hidden">
                    <AnimatePresence mode="popLayout">
                        {visibleNotifications.map((notification) => (
                            <AlertNotificationCard
                                key={alertKey(notification)}
                                notification={notification}
                                onClose={removeNotification}
                                shouldReduceMotion={shouldReduceMotion}
                            />
                        ))}
                    </AnimatePresence>
                </div>

                <div className="h-9">
                    <AnimatePresence>
                        {totalCount > MAX_VISIBLE_NOTIFICATIONS && (
                            <motion.div
                                layout={!shouldReduceMotion}
                                initial={
                                    shouldReduceMotion
                                        ? false
                                        : VIEW_MORE_INITIAL
                                }
                                animate={CONTAINER_ANIMATE}
                                exit={
                                    shouldReduceMotion
                                        ? CARD_EXIT_REDUCED
                                        : VIEW_MORE_EXIT
                                }
                                transition={getViewMoreTransition(
                                    shouldReduceMotion,
                                )}
                                className="flex items-center justify-between self-stretch rounded-[5px] bg-white/10 p-2 backdrop-blur-sm"
                            >
                                <div
                                    role="button"
                                    tabIndex={0}
                                    aria-label={t('button.viewAll')}
                                    onClick={handleViewAllAlerts}
                                    onKeyDown={handleViewAllKeyDown}
                                    className="flex cursor-pointer items-center gap-1.5 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                                >
                                    <span className="text-sm font-medium text-white-100">
                                        {t('page.alertsCount', {
                                            count: totalCount,
                                        })}
                                    </span>
                                    <span className="flex items-center gap-[3px] text-sm font-medium text-grey-200">
                                        <span>{t('button.viewAll')}</span>
                                        <CaretRightIcon
                                            className="size-4"
                                            weight="bold"
                                        />
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    aria-label={t('button.clear')}
                                    onClick={clearNotifications}
                                    className="flex size-6 cursor-pointer items-center justify-center rounded text-grey-200 hover:bg-white/10 hover:text-white-100 active:scale-[0.95] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                                >
                                    <XIcon className="size-4" weight="bold" />
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.div>
    );
}
